import { apiClient } from './client';
import type { ApiSuccess } from '../types';

export interface BusinessVertical {
  id: number;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  status: string;
}

export interface VerticalAccess {
  id: number;
  userId: number;
  verticalId: number;
  verticalCode: string;
  verticalName: string;
  roleId: number | null;
  reportsToUserId: number | null;
  reportsToName: string | null;
}

export interface TeamMember {
  accessId: number;
  userId: number;
  userName: string;
  email: string;
  verticalId: number;
  verticalCode: string;
}

export interface TeamPerformanceMember {
  userId: number;
  userName: string;
  email: string;
  target: number;
  achievedRevenue: number;
  achievementPct: number;
  workItems: number;
}

export interface TeamPerformance {
  vertical: { id: number; code: string; name: string; status: string };
  year: number;
  headUserId: number;
  totals: { teamSize: number; target: number; achievedRevenue: number; workItems: number; achievementPct: number };
  members: TeamPerformanceMember[];
}

export const verticalsApi = {
  /** The verticals the current user may access (drives the switcher). */
  async listAccessible(): Promise<BusinessVertical[]> {
    return (await apiClient.get<ApiSuccess<BusinessVertical[]>>('/business-verticals')).data.data;
  },

  // Admin: per-user vertical access (business_vertical.manage).
  async listAccess(userId: number): Promise<VerticalAccess[]> {
    return (await apiClient.get<ApiSuccess<VerticalAccess[]>>('/user-vertical-access', { params: { userId } })).data.data;
  },
  async assignAccess(userId: number, verticalId: number, reportsToUserId?: number | null): Promise<VerticalAccess> {
    return (await apiClient.post<ApiSuccess<VerticalAccess>>('/user-vertical-access', { userId, verticalId, reportsToUserId: reportsToUserId ?? null })).data.data;
  },
  async revokeAccess(id: number): Promise<void> {
    await apiClient.delete(`/user-vertical-access/${id}`);
  },
  /** Set/clear the reporting head (Business Head) for a grant. */
  async setReportsTo(id: number, reportsToUserId: number | null): Promise<VerticalAccess> {
    return (await apiClient.put<ApiSuccess<VerticalAccess>>(`/user-vertical-access/${id}/reports-to`, { reportsToUserId })).data.data;
  },
  /** The team reporting to a head within a vertical. */
  async listTeam(verticalId: number, headUserId?: number): Promise<TeamMember[]> {
    return (await apiClient.get<ApiSuccess<TeamMember[]>>('/user-vertical-access/team', { params: { verticalId, headUserId } })).data.data;
  },
  /** Business Head team performance overview. */
  async teamPerformance(params: { verticalId?: number; headUserId?: number; year?: number }): Promise<TeamPerformance> {
    return (await apiClient.get<ApiSuccess<TeamPerformance>>('/business-verticals/team-performance', { params })).data.data;
  },

  // Super Admin: phased go-live control (business_vertical.golive).
  /** ALL verticals incl. inactive — for the go-live management screen. */
  async listAllForManage(): Promise<BusinessVertical[]> {
    return (await apiClient.get<ApiSuccess<BusinessVertical[]>>('/business-verticals/manage')).data.data;
  },
  /** Flip a vertical live (active) / off (inactive). */
  async setStatus(id: number, status: 'active' | 'inactive'): Promise<BusinessVertical> {
    return (await apiClient.put<ApiSuccess<BusinessVertical>>(`/business-verticals/${id}/status`, { status })).data.data;
  },
};
